import type {Action} from '@github-ui/action-bar'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {AddToProjectsBulkAction} from '@github-ui/issues-bulk-actions/AddToProjectsBulkAction'
import {ApplyAssigneesBulkAction} from '@github-ui/issues-bulk-actions/ApplyAssigneesBulkAction'
import {ApplyIssueTypeBulkAction as ApplyIssueTypeAction} from '@github-ui/issues-bulk-actions/ApplyIssueTypeBulkAction'
import {ApplyLabelsBulkAction} from '@github-ui/issues-bulk-actions/ApplyLabelsBulkAction'
import {ApplyMilestoneBulkAction} from '@github-ui/issues-bulk-actions/ApplyMilestoneBulkAction'
import {BulkMarkAs} from '@github-ui/issues-bulk-actions/BulkMarkAs'
import type {AppPayload} from '@github-ui/issues-shared/app-payload'
import {LABELS} from '@github-ui/issues-shared/labels'
import {VALUES} from '@github-ui/issues-shared/values'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {useListViewMultiPageSelection} from '@github-ui/list-view/ListViewMultiPageSelectionContext'
import {useListViewSelection} from '@github-ui/list-view/ListViewSelectionContext'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {
  type Icon,
  IssueOpenedIcon,
  MilestoneIcon,
  PeopleIcon,
  ProjectSymlinkIcon,
  RocketIcon,
  TagIcon,
  TriangleDownIcon,
} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import type {ReactNode} from 'react'
import {Suspense, useCallback, useImperativeHandle, useMemo, useState} from 'react'

import {BUTTON_LABELS} from '../../../constants/buttons'
import {useActiveSearchQuery, useIsQueryLoading, useQueryBulk} from '../../../contexts/QueryContext'
import {guardOverflowMenuChange} from '../../../utils/guard-overflow-menu-change'
import type {IssueNodeType, ItemNodeType} from '../ListItems'
import type {ListItemsHeaderProps} from './ListItemsHeader'

function isIssueNode(node: ItemNodeType): node is IssueNodeType {
  return node.__typename === 'Issue'
}

function ListItemsHeaderBulkFallback({
  issueTypesEnabled,
  onToggleSelectAll,
  selectionAction,
}: {
  issueTypesEnabled?: boolean
  onToggleSelectAll: (isSelectAllChecked: boolean) => void
  selectionAction?: ReactNode
}) {
  const renderFake = useCallback((isOverflowing: boolean, LeadingVisual: Icon, anchorText: string) => {
    if (isOverflowing) {
      return (
        <ActionList.Item disabled>
          <ActionList.LeadingVisual>
            <LeadingVisual />
          </ActionList.LeadingVisual>
          {anchorText}
          <ActionList.TrailingVisual>
            <TriangleDownIcon />
          </ActionList.TrailingVisual>
        </ActionList.Item>
      )
    }

    return (
      <Button disabled leadingVisual={LeadingVisual} trailingVisual={TriangleDownIcon}>
        {anchorText}
      </Button>
    )
  }, [])

  // This array should be in sync with the actions array populating the actual component
  const actions = useMemo(
    () => [
      {
        key: 'mark-as',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, VALUES.issueIcons.CLOSED.icon, 'Mark as'),
      },
      {
        key: 'apply-labels',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, TagIcon, 'Label'),
      },
      {
        key: 'apply-assignees',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, PeopleIcon, 'Assign'),
      },
      {
        key: 'add-to-projects',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, ProjectSymlinkIcon, 'Project'),
      },
      {
        key: 'apply-milestone',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, MilestoneIcon, 'Milestone'),
      },
      ...(issueTypesEnabled
        ? [
            {
              key: 'apply-issue-type',
              render: (isOverflowMenu: boolean) =>
                renderFake(isOverflowMenu, IssueOpenedIcon, BUTTON_LABELS.setIssueType),
            },
          ]
        : []),
    ],
    [renderFake, issueTypesEnabled],
  )

  return (
    <ListViewMetadata
      onToggleSelectAll={onToggleSelectAll}
      selectionAction={selectionAction}
      actionsLabel={LABELS.bulkActions}
      actions={actions}
      density={'normal'}
    />
  )
}

export function ListItemsHeaderWithBulkActions({
  checkedItems,
  issueCount,
  issueNodes,
  setCheckedItems,
  useBulkActions,
  listHasPRs,
  issueTypesEnabled,
  querySelectionResetRef,
}: ListItemsHeaderProps) {
  const {scoped_repository} = useAppPayload<AppPayload>()

  const {addToast, addPersistedToast} = useToastContext()
  const {setBulkJobId, bulkJobId} = useQueryBulk()

  const issuesToActOn = useMemo(
    () => issueNodes.filter(node => node != null && checkedItems.has(node.id) && isIssueNode(node)),
    [checkedItems, issueNodes],
  )

  const [isBulkActionRunning, setIsBulkActionRunning] = useState(bulkJobId != null)
  const activeSearchQuery = useActiveSearchQuery()
  const isQueryLoading = useIsQueryLoading()
  const {setSelectedCount} = useListViewSelection()
  const {setMultiPageSelectionAllowed} = useListViewMultiPageSelection()

  const bulkActionCompleted = useCallback(
    (jobId?: string) => {
      setIsBulkActionRunning(false)
      if (jobId) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({
          type: 'info',
          message: LABELS.updatingIssues,
          icon: <RocketIcon />,
          role: 'status',
        })
        setBulkJobId(jobId)
      }
    },
    [addPersistedToast, setBulkJobId],
  )

  const bulkActionError = useCallback(
    (error: Error) => {
      setIsBulkActionRunning(false)
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message: `Could not update issues: ${error.message}`,
      })
    },
    [addToast],
  )

  const visibleIssueCount = issueNodes.filter(node => node && isIssueNode(node)).length
  const allVisibleIssuesSelected = issuesToActOn.length > 0 && issuesToActOn.length === visibleIssueCount
  const querySelectionEnabled = isFeatureEnabled('issues_react_bulk_actions_by_query')
  const [isAllMatchingSelected, setIsAllMatchingSelected] = useState(false)
  const canSelectAllMatching = querySelectionEnabled && allVisibleIssuesSelected && issueCount > visibleIssueCount

  const resetQuerySelection = useCallback(() => {
    setIsAllMatchingSelected(false)
    setMultiPageSelectionAllowed?.(false)
  }, [setMultiPageSelectionAllowed])

  useImperativeHandle(querySelectionResetRef, () => resetQuerySelection, [resetQuerySelection])

  const sharedActionProps = useMemo(
    () => ({
      onCompleted: bulkActionCompleted,
      onError: bulkActionError,
      disabled: isBulkActionRunning,
      issuesToActOn: issuesToActOn.map(node => node.id),
      queryTarget:
        isAllMatchingSelected && allVisibleIssuesSelected && scoped_repository
          ? {query: activeSearchQuery, repositoryId: scoped_repository.id}
          : undefined,
    }),
    [
      isAllMatchingSelected,
      allVisibleIssuesSelected,
      bulkActionCompleted,
      bulkActionError,
      isBulkActionRunning,
      issuesToActOn,
      activeSearchQuery,
      scoped_repository,
    ],
  )

  const onToggleSelectAll = useCallback(
    (isSelectAllChecked: boolean) => {
      if (isSelectAllChecked) {
        const allItems = issueNodes.reduce<Set<string>>((set, node) => {
          if (node != null) set.add(node.id)
          return set
        }, new Set<string>())
        setCheckedItems(allItems)
      } else {
        setCheckedItems(new Set())
        resetQuerySelection()
        setSelectedCount(0)
      }
    },
    [issueNodes, resetQuerySelection, setCheckedItems, setSelectedCount],
  )

  const selectAllMatching = useCallback(() => {
    if (!canSelectAllMatching || isAllMatchingSelected) return

    setIsAllMatchingSelected(true)
    setSelectedCount(issueCount)
    setMultiPageSelectionAllowed?.(true)
  }, [canSelectAllMatching, isAllMatchingSelected, issueCount, setMultiPageSelectionAllowed, setSelectedCount])

  const selectionAction = canSelectAllMatching ? (
    <Button variant="invisible" size="small" disabled={isAllMatchingSelected} onClick={selectAllMatching}>
      Select all {issueCount} matching issues
    </Button>
  ) : undefined

  const shouldRenderBulkActions = scoped_repository && useBulkActions && !listHasPRs

  const scopedOwner = scoped_repository?.owner
  const scopedName = scoped_repository?.name

  const analyticsMetadata = useMemo(
    () => ({
      owner: scopedOwner ?? '',
      repositoryName: scopedName ?? '',
    }),
    [scopedName, scopedOwner],
  )
  const [overflowMenuOpen, setOverflowMenuOpen] = useState<boolean | undefined>(undefined)

  const overflowMenuProps = useMemo(() => {
    // see https://github.com/github/primer/issues/5896#issuecomment-3335244847
    const onOpenChange = (open: boolean) => guardOverflowMenuChange(open, setOverflowMenuOpen)
    return {open: overflowMenuOpen, onOpenChange}
  }, [overflowMenuOpen])

  // This array should be in sync with the actions array populating the fallback component
  const actions = useMemo<Action[] | undefined>(() => {
    if (!shouldRenderBulkActions || !scopedOwner || !scopedName) return
    const issueIds: string[] = []
    const issueNumbers: number[] = []
    for (const issue of issuesToActOn) {
      issueIds.push(issue.id)
      issueNumbers.push(issue.number)
    }
    const actionList = [
      {
        key: 'mark-as',
        render: (isOverflowMenu: boolean) => <BulkMarkAs {...sharedActionProps} nested={isOverflowMenu} />,
      },
      {
        key: 'apply-labels',
        render: (isOverflowMenu: boolean) => (
          <ApplyLabelsBulkAction
            owner={scopedOwner}
            repo={scopedName}
            nested={isOverflowMenu}
            issueIds={issueIds}
            {...sharedActionProps}
          />
        ),
      },
      {
        key: 'apply-assignees',
        render: (isOverflowMenu: boolean) => (
          <ApplyAssigneesBulkAction
            nested={isOverflowMenu}
            issueIds={issueIds}
            issueNumbers={issueNumbers}
            {...sharedActionProps}
            owner={scopedOwner}
            repo={scopedName}
          />
        ),
      },
      {
        key: 'add-to-projects',
        render: (isOverflowMenu: boolean) => (
          <AddToProjectsBulkAction
            nested={isOverflowMenu}
            issueIds={issueIds}
            owner={scopedOwner}
            repo={scopedName}
            {...sharedActionProps}
          />
        ),
      },
      {
        key: 'apply-milestone',
        render: (isOverflowMenu: boolean) => (
          <ApplyMilestoneBulkAction
            owner={scopedOwner}
            repo={scopedName}
            nested={isOverflowMenu}
            issueIds={issueIds}
            {...sharedActionProps}
          />
        ),
      },
    ]

    if (issueTypesEnabled)
      actionList.push({
        key: 'apply-issue-type',
        render: (isOverflowMenu: boolean) => (
          <AnalyticsProvider appName="issue_types" category="issues_index" metadata={analyticsMetadata}>
            <ApplyIssueTypeAction
              owner={scopedOwner}
              repo={scopedName}
              nested={isOverflowMenu}
              issueIds={issueIds}
              {...sharedActionProps}
            />
          </AnalyticsProvider>
        ),
      })

    return actionList
  }, [
    shouldRenderBulkActions,
    sharedActionProps,
    scopedOwner,
    scopedName,
    issueTypesEnabled,
    issuesToActOn,
    analyticsMetadata,
  ])

  return (
    <Suspense
      fallback={
        <ListItemsHeaderBulkFallback
          issueTypesEnabled={issueTypesEnabled}
          onToggleSelectAll={onToggleSelectAll}
          selectionAction={selectionAction}
        />
      }
    >
      <ListViewMetadata
        onToggleSelectAll={onToggleSelectAll}
        selectionAction={selectionAction}
        actionsLabel={LABELS.bulkActions}
        actions={actions}
        density={'normal'}
        assistiveAnnouncement={isQueryLoading ? LABELS.loadingQueryResults : undefined}
        overflowMenuProps={overflowMenuProps}
      />
    </Suspense>
  )
}
